"use client";
import React, { useState, useEffect } from "react";
import { HeartIcon as HeartIconSolid, PlayIcon } from '@heroicons/react/24/solid'; // Solid for liked

import { HeartIcon as HeartOutline } from "@heroicons/react/24/outline";
import { fetchNewsLikes } from "@/actions/news";
import { useAuthUserContext } from "@/contexts/authUserContext";
import AuthGuard from "../AuthGuard";
import { likePost } from "@/actions/user_specific";
import { indexDBClient } from "@/lib/index_db";
import { trackClick } from "@/lib/analytics";

interface LikeButtonProps {
  cardId: string;
  likeCount: number;
}

const LikeButton: React.FC<LikeButtonProps> = ({ cardId, likeCount }) => {
  const [isLiked, setIsLiked] = useState(false);
  const [_likeCount, setLikeCount] = useState(likeCount);
  const [loading, setLoading] = useState(false);
  const { user, isAuthenticated } = useAuthUserContext();

  useEffect(() => {
    if (!isAuthenticated) return;

    let isMounted = true;
    // Check local IndexedDB for like state first
    indexDBClient.getCard(cardId).then((card: any) => {
      if (isMounted && card && typeof card.isLiked === 'boolean') {
        setIsLiked(card.isLiked);
      } else {
        // Fallback to server fetch if not in IndexedDB
        fetchNewsLikes(cardId)
          .then((data) => {
            if ('error' in data) return 
            let is_liked = data.user_list.find(el => el.id === user?.uid);
            if (is_liked) {
              setIsLiked(is_liked);
              indexDBClient.addCard({ id: cardId, isLiked: is_liked });
            }
            }
          )
          .catch(() => {});
      }
    });
    return () => { isMounted = false; };
  }, [cardId, likeCount, isAuthenticated, user]);

  const handleLike = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    trackClick('like_button_click', undefined,undefined, isLiked ? "Unliked Video" : "Liked Video", { cardId });
    try {
      const newLiked = !isLiked;
      setIsLiked(newLiked);
      setLikeCount((c) => Math.max(0, newLiked ? c + 1 : c - 1));
      // Update IndexedDB immediately for instant UI
      await indexDBClient.addCard({ id: cardId, isLiked: newLiked });
      await likePost({ [newLiked ? "like_ids" : "unlike_ids"]: [cardId] }, user?.uid || "");
    } catch {
      // Optionally show error toast
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthGuard loginMessage="Log in to like this video">
      <button
        className={`flex items-center space-x-1 hover:text-primary transition-colors duration-200 ${isLiked ? "text-primary" : "text-gray-600"}`}
        onClick={handleLike}
        // disabled={loading}
        aria-pressed={isLiked}
        type="button"
      >
      {isLiked ? (
        <HeartIconSolid className="h-6 w-6" />
      ) : (
        <HeartOutline className="h-6 w-6" />
      )}
      <span className="text-sm font-medium">{_likeCount > 0 ? _likeCount : ""}</span>
      </button>
    </AuthGuard>
  );
};

export default LikeButton;
